import { Alert, CircularProgress, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useAppSelector } from "store/store";
import { selectScrapViewportRange } from "store/slices/scrapTrackingViewportSlice";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import type { JobFailureWindow } from "src/store/api/generatedApi";
import { useGetJobFailuresQuery } from "src/store/api/generatedApi";
import { JobFailuresTimeline } from "./JobFailuresTimeline";
import { ResetTerminalFailuresDialog } from "./ResetTerminalFailuresDialog";
import { SummaryChip } from "./SummaryChip";

type JobFailuresSectionProps = Readonly<{
  selectedLocation: string;
  enabledAngleNames: string[];
  selectedLabellingName: string | null;
}>;

export const JobFailuresSection = ({
  selectedLocation,
  enabledAngleNames,
  selectedLabellingName,
}: JobFailuresSectionProps) => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();
  const viewportRange = useAppSelector(selectScrapViewportRange);
  const [clickedWindow, setClickedWindow] = useState<JobFailureWindow | null>(
    null
  );

  const handleWindowClick = useCallback((window: JobFailureWindow) => {
    if (window.terminal_failure_count > 0) {
      setClickedWindow(window);
    }
  }, []);

  const start = viewportRange.start;
  const end = viewportRange.end;

  const anglesJson =
    enabledAngleNames.length > 0
      ? JSON.stringify(enabledAngleNames)
      : undefined;

  const {
    data: failuresData,
    isLoading,
    isError,
  } = useGetJobFailuresQuery(
    {
      tenantName: tenant,
      start: start.toISOString(),
      end: end.toISOString(),
      targetLocation: selectedLocation,
      anglesJson,
      labellingName: selectedLabellingName ?? undefined,
    },
    { skip: !selectedLocation }
  );

  const windows = useMemo(
    () => failuresData?.windows ?? [],
    [failuresData?.windows]
  );

  const summary = useMemo(() => {
    const total = windows.length;
    const withTerminal = windows.filter(
      (w) => w.terminal_failure_count > 0
    ).length;
    const withRetriable = windows.filter(
      (w) => w.retriable_failure_count > 0 && w.terminal_failure_count === 0
    ).length;
    const healthy = total - withTerminal - withRetriable;
    return { total, withTerminal, withRetriable, healthy };
  }, [windows]);

  if (!selectedLocation) {
    return null;
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack p={2}>
        <Alert severity="error">{t("jobFailuresLoadError")}</Alert>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 3,
        backgroundColor: "grey.50",
        borderRadius: 2,
        border: 1,
        borderColor: "grey.300",
      }}
    >
      <Typography variant="h5" fontWeight={600} color="text.primary">
        {t("jobFailuresTitle")}
      </Typography>

      <Stack direction="row" spacing={4}>
        <SummaryChip label={t("jobFailuresTotal")} value={summary.total} />
        <SummaryChip
          label={t("jobFailureTerminal")}
          value={summary.withTerminal}
          color="error.main"
        />
        <SummaryChip
          label={t("jobFailureRetriable")}
          value={summary.withRetriable}
          color="warning.main"
        />
        <SummaryChip
          label={t("jobFailuresHealthy")}
          value={summary.healthy}
          color="success.main"
        />
      </Stack>

      {windows.length === 0 ? (
        <Alert severity="success">{t("jobFailuresNoFailures")}</Alert>
      ) : (
        <JobFailuresTimeline
          windows={windows}
          start={start}
          end={end}
          onWindowClick={handleWindowClick}
        />
      )}

      <ResetTerminalFailuresDialog
        open={clickedWindow !== null}
        onClose={() => {
          setClickedWindow(null);
        }}
        clickedWindow={clickedWindow}
        targetLocation={selectedLocation}
        enabledAngleNames={enabledAngleNames}
        selectedLabellingName={selectedLabellingName}
        viewportStart={start.toISOString()}
        viewportEnd={end.toISOString()}
      />
    </Stack>
  );
};
